import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Kolom, Suggesties } from '@xxtract/tabelfilters';
import { useKlantenlijst } from '@/features/processes/hooks/useKlantenlijst';
import { CONTROL_CLASS, MELDING_CLASS, POPUP_CLASS, regelClass } from './stijl';

const WIS_ICON = 'close';
const MAX_REGELS = 10;
/** Een volledig klantnummer (GLN). */
const VOLLEDIG_GLN = /^\d{13}$/;

interface TekstFilterProps {
  /** Alleen de beschrijving van de kolom; `never` laat elke rijvorm toe. */
  kolom: Kolom<never, never>;
  /** De filterwaarde uit de lijststand. */
  tekst: string;
  onChange: (tekst: string) => void;
  suggesties: (tekst: string) => Suggesties;
  /** 0 = direct (browser), 300 = na 300 ms stilte (server, §3). */
  vertraging?: number;
  /** Meldt focus, zodat een serverlijst suggesties voor dit veld meevraagt. */
  onFocusWissel?: (focus: boolean) => void;
}

interface Regel {
  waarde: string;
  naam: string;
  nummer?: string;
  /** Een getypt klantnummer dat niet in de klantenlijst staat. */
  onbekend?: boolean;
}

/**
 * Tekstfilter als combobox naar het voorbeeld van `KlantZoeker.tsx` (CAP-4, §3): vrij typen, met een
 * lijst bestaande waarden die krimpt. `alleenKeuze` filtert pas na een keuze en Escape zet die terug.
 * Het veld is één blijvende `<input>`: tijdens typen wordt niets herbouwd en springt de cursor nergens heen.
 */
export function TekstFilter({ kolom, tekst, onChange, suggesties, vertraging = 0, onFocusWissel }: TekstFilterProps) {
  const { t } = useTranslation();
  const id = useId();
  const lijstId = `${id}-lijst`;
  const [invoer, setInvoer] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [actief, setActief] = useState(-1);
  const timer = useRef<number | null>(null);

  // Een nieuwe filterwaarde (keuze, reset, andere stand) vervangt wat er getypt stond.
  // Behalve als het veld die waarde zelf meldde (terugval zonder klantenlijst): dan blijven de getypte cijfers staan.
  const [vorige, setVorige] = useState(tekst);
  const [eigenMelding, setEigenMelding] = useState<string | null>(null);
  if (vorige !== tekst) {
    setVorige(tekst);
    if (tekst !== eigenMelding) setInvoer(null);
    setEigenMelding(null);
  }

  const vanKlanten = kolom.bron === 'klantenlijst';
  const klantenlijst = useKlantenlijst(vanKlanten);
  const klanten = klantenlijst?.data;
  const laadt = Boolean(klantenlijst?.isLoading);
  // Zonder namenlijst valt het veld terug op het oude KlantZoeker-gedrag: dertien cijfers filteren direct.
  const klantFout = vanKlanten && Boolean(klantenlijst?.isError);
  const weergave = vanKlanten && tekst ? (klanten?.find((k) => k.gln === tekst)?.companyName ?? tekst) : tekst;
  const waarde = invoer ?? weergave;
  const zoekterm = waarde.trim();
  const zoekt = !klantFout && zoekterm.length >= (kolom.minTekens ?? 0);

  let regels: Regel[] = [];
  let meer = false;
  if (open && zoekt) {
    if (vanKlanten) {
      const term = zoekterm.toLocaleLowerCase('nl');
      const treffers = (klanten ?? []).filter((k) => k.companyName.toLocaleLowerCase('nl').includes(term) || k.gln.includes(term));
      const alles: Regel[] = treffers.map((k) => ({ waarde: k.gln, naam: k.companyName, nummer: k.gln }));
      // Een volledig getypt nummer is altijd zelf een keuze (spec-klant-zoeken-op-naam CAP-1); hij telt mee voor "meer".
      if (VOLLEDIG_GLN.test(zoekterm) && !treffers.some((k) => k.gln === zoekterm)) alles.unshift({ waarde: zoekterm, naam: zoekterm, onbekend: true });
      regels = alles.slice(0, MAX_REGELS);
      meer = alles.length > MAX_REGELS;
    } else {
      const s = suggesties(zoekterm);
      regels = s.waarden.map((w) => ({ waarde: w, naam: w }));
      meer = s.meer;
    }
  }
  const toontLijst = open && regels.length > 0;

  // Een filterwaarde die van buiten verandert (reset, keuze, andere stand) laat oude getypte tekst niet meer na-ijlen.
  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, [tekst]);

  const stopTimer = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const meld = (nieuw: string) => {
    stopTimer();
    onChange(nieuw);
  };

  const kies = (regel: Regel) => {
    meld(regel.waarde);
    setInvoer(null);
    setOpen(false);
    setActief(-1);
  };

  const typ = (nieuw: string) => {
    setInvoer(nieuw);
    if (klantFout) {
      // Terugtypen naar iets anders haalt het filter weer weg, zodat veld en scherm gelijk blijven.
      const nummer = nieuw.trim();
      const gemeld = VOLLEDIG_GLN.test(nummer) ? nummer : '';
      setEigenMelding(gemeld);
      meld(gemeld);
      return;
    }
    setOpen(true);
    // Na typen is bij `alleenKeuze` de eerste regel actief, zodat Enter of Tab hem kiest (KlantZoeker); bij focus niet.
    setActief(kolom.alleenKeuze ? 0 : -1);
    if (kolom.alleenKeuze) return;
    if (vertraging > 0) {
      stopTimer();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        onChange(nieuw);
      }, vertraging);
    } else {
      onChange(nieuw);
    }
  };

  const wis = () => {
    meld('');
    setInvoer(null);
    setOpen(false);
  };

  const toets = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (toontLijst || open) {
        setOpen(false);
        setActief(-1);
      } else if (kolom.alleenKeuze) {
        setInvoer(null);
      } else {
        wis();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActief((i) => Math.min(i + 1, regels.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActief((i) => Math.max(i - 1, 0));
      return;
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && toontLijst && regels[actief]) {
      if (e.key === 'Enter') e.preventDefault();
      kies(regels[actief]!);
      return;
    }
    // Tab zonder actieve regel verlaat het veld en verandert niets.
    if (e.key === 'Tab') setOpen(false);
  };

  return (
    <div className="relative w-full md:w-56">
      <input
        aria-activedescendant={toontLijst && regels[actief] ? `${id}-${actief}` : undefined}
        aria-autocomplete="list"
        aria-controls={lijstId}
        aria-expanded={toontLijst}
        aria-label={t(kolom.kopSleutel)}
        autoComplete="off"
        className={`${CONTROL_CLASS} w-full ${waarde ? 'pr-11 md:pr-8' : ''}`}
        onBlur={() => {
          if (kolom.alleenKeuze) setInvoer(null);
          setOpen(false);
          setActief(-1);
          onFocusWissel?.(false);
        }}
        onChange={(e) => typ(e.target.value)}
        onFocus={() => {
          setOpen(true);
          onFocusWissel?.(true);
        }}
        onKeyDown={toets}
        placeholder={t(kolom.kopSleutel)}
        role="combobox"
        type="text"
        value={waarde}
      />

      {waarde ? (
        <button
          aria-label={t('tabel.tekstWissen', { kolom: t(kolom.kopSleutel) })}
          className="absolute right-0 top-0 inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-control)] text-[var(--foreground-muted)] hover:text-[var(--foreground)] md:right-1 md:h-9 md:w-7"
          onClick={wis}
          onMouseDown={(e) => e.preventDefault()}
          type="button"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">{WIS_ICON}</span>
        </button>
      ) : null}

      <span aria-live="polite" className="sr-only">
        {toontLijst ? t('tabel.treffers', { count: regels.length }) : ''}
      </span>

      {/* De lijst staat er altijd, zodat `aria-controls` nooit naar niets wijst. */}
      <div className={toontLijst || (open && zoekt && !laadt) ? POPUP_CLASS : 'hidden'}>
        <ul className="max-h-64 overflow-y-auto" id={lijstId} role="listbox">
          {toontLijst
            ? regels.map((r, i) => (
                <li
                  aria-selected={i === actief}
                  className={regelClass(i === actief)}
                  id={`${id}-${i}`}
                  key={r.waarde}
                  onClick={() => kies(r)}
                  onMouseDown={(e) => e.preventDefault()}
                  ref={i === actief ? (el) => el?.scrollIntoView?.({ block: 'nearest' }) : undefined}
                  role="option"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{r.naam}</span>
                    {r.onbekend || r.nummer ? (
                      <span className="block text-xs text-[var(--foreground-muted)]">{r.onbekend ? t('processes.filters.klantOnbekend') : r.nummer}</span>
                    ) : null}
                  </span>
                </li>
              ))
            : null}
        </ul>
        {toontLijst && meer ? <p className={`${MELDING_CLASS} border-t border-[var(--border)]`}>{t('tabel.meerTreffers')}</p> : null}
        {open && zoekt && !laadt && regels.length === 0 ? (
          <p className={MELDING_CLASS} role="status">{t('tabel.geenTreffers')}</p>
        ) : null}
      </div>
      {klantFout ? <p className="mt-1 text-xs text-[var(--foreground-muted)]" role="status">{t('processes.filters.klantNamenWeg')}</p> : null}
    </div>
  );
}
